'use client';

import React from 'react';
import Link from 'next/link';

const ApertureMark = ({ size = 20, color = '#1F4D3F' }: { size?: number; color?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.4"
    strokeLinecap="round"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <g transform="rotate(0 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(60 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(120 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(180 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(240 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(300 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
  </svg>
);

const footerLinks = {
  Products: [
    { label: 'POS', href: 'https://vasy.ai/best-pos-billing-retail-software' },
    { label: 'Accounting', href: 'https://vasy.ai/best-gst-accounting-software-retail' },
    { label: 'ERP', href: 'https://vasy.ai/best-erp-for-retail-stores-india' },
    { label: 'Inventory Management', href: 'https://vasy.ai/cloud-based-inventory-management-software' },
    { label: 'CRM', href: 'https://vasy.ai/cloud-based-crm-for-retail-business' },
    { label: 'Omnichannel E-Commerce', href: 'https://vasy.ai/best-erp-for-ecommerce-omnichannel' },
  ],
  Industries: [
    { label: 'Supermarket', href: 'https://vasy.ai/retail/cloud-based-supermarket-billing-software' },
    { label: 'Readymade Garment', href: 'https://vasy.ai/retail/garment-software-india' },
    { label: 'Hypermarket', href: 'https://vasy.ai/retail/hypermarket-billing-software' },
    { label: 'Stationery', href: 'https://vasy.ai/retail/cloud-based-stationery-billing-software' },
    { label: 'Bakery', href: 'https://vasy.ai/retail/cloud-based-bakery-shop-billing-software' },
    { label: 'Sweet', href: 'https://vasy.ai/retail/cloud-based-sweet-shop-billing-software' },
  ],
  Company: [
    { label: 'The Retail Guru', href: 'https://vasy.ai/the-retail-guru/' },
    { label: 'About Us', href: 'https://vasy.ai/about-us' },
    { label: 'Career', href: 'https://vasy.ai/career/' },
    { label: 'Contact Us', href: 'https://vasy.ai/contact-us' },
  ],
};

export default function Footer() {
  return (
    <footer
      className="relative overflow-hidden pt-20 pb-8"
      role="contentinfo"
      style={{
        background: 'linear-gradient(180deg, #F7F6F1 0%, #F2EFE8 100%)',
      }}
    >
      {/* Soft top border */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'rgba(211,223,219,0.9)' }}
        aria-hidden="true"
      />
      {/* Subtle ambient */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center top, rgba(79,155,122,0.06) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top: brand + tagline */}
        <div
          className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10 mb-16 pb-16"
          style={{ borderBottom: '1px solid rgba(211,223,219,0.9)' }}
        >
          <div className="max-w-sm">
            <div
              className="flex items-center gap-2 w-fit mb-5"
              style={{
                background: 'rgba(255,255,255,0.72)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(221,231,227,0.8)',
                borderRadius: '13px',
                padding: '9px 16px',
              }}
            >
              <ApertureMark size={20} color="#1F4D3F" />
              <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.33px', color: '#16352D' }}>
                vina
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-2" style={{ color: '#4F665F' }}>
              Business intelligence that understands your business.
            </p>
            <p
              className="text-sm leading-relaxed"
              style={{ color: '#7B8F88', fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.02em' }}
            >
              A product by Vasy.
            </p>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {Object.entries(footerLinks).map(([group, links]) => (
              <div key={group}>
                <h3
                  className="mb-4"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '10px',
                    letterSpacing: '0.08em',
                    color: '#8A9C95',
                    textTransform: 'uppercase',
                  }}
                >
                  {group}
                </h3>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm transition-colors"
                        style={{ color: '#4F665F', fontWeight: 400 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#16352D')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#4F665F')}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom strip */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#8A9C95', letterSpacing: '0.02em' }}>
            © 2026 Vasy · All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              href="https://vasy.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#8A9C95', letterSpacing: '0.02em' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#4F665F')}
              onMouseLeave={e => (e.currentTarget.style.color = '#8A9C95')}
            >
              Privacy
            </Link>
            <Link
              href="https://vasy.ai/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#8A9C95', letterSpacing: '0.02em' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#4F665F')}
              onMouseLeave={e => (e.currentTarget.style.color = '#8A9C95')}
            >
              Terms
            </Link>
            <Link
              href="#trust"
              className="transition-colors"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#8A9C95', letterSpacing: '0.02em' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#4F665F')}
              onMouseLeave={e => (e.currentTarget.style.color = '#8A9C95')}
            >
              Security
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}